import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { addDays, format } from "date-fns";
import { AlertTriangle, ArrowLeft, CalendarDays, Check, Eraser, PenLine, Printer, Send } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import PageFrame from "@/components/PageFrame";
import OwnershipModal from "@/components/OwnershipModal";
import { LAYOUT } from "@/data/affidavits";
import {
  LEVEL_NAMES,
  MIN_QSE_BLACK,
  formatYearEnd,
  isQse,
  latestFebruaryYearEnd,
  levelFor,
  pct,
  yearEndChoices,
} from "@/data/ownership";
import { detailBaseSize, detailValues, fitDetail } from "@/data/details";
import { invoiceRows, newRequestId } from "@/data/invoice";
import { api } from "@/api";
import { useAuth } from "@/auth";

// Signing page: the affidavit's own pages (images of the original PDF), with the signature box on
// the Deponent Signature line and a handwritten date on the Date line. "Print to PDF and Send" sends
// the signature + date to the backend, which stamps them onto the ORIGINAL PDF (with the Commissioner
// of Oaths' signature and stamps) and emails it. Owner: the signed PDF is never shown, downloaded or
// printed on the website - only the email has it; the page shows the recipients, then OK -> front page.
// Commissioner of Oaths (owner): Schalk Coetzee and Ruan England sign with their own signature and
// stamp; any other user chooses one of them ("Choose your Commissioner" above the commissioner line).

const ADMIN_EMAIL = "admin@tcauditors.co.za";

const INK = "#000000";          // black pen for the deponent's signature (owner)
const DATE_INK = "#000000";     // handwritten date: black (owner; backend HAND_INK)
const HAND_BOLD = 0.06;         // backend HAND_BOLD: outline (x font size) - thick pen, as the signatures
const DATE_FONT_PT = 13;        // backend DATE_FONT_SIZE
const DATE_BASELINE_UP = 4;     // backend DATE_BASELINE_UP
const DATE_X_PAD = 6;           // backend: dt["x"] + 6
const EXPORT_W = 600;           // signature PNG width sent to the backend
// Owner: the deponent signs comfortably on a big pad; the signature is then redrawn small on the
// Deponent Signature line, at a natural size with an even pen line
const PEN_PT = 1.3;             // pen line on the affidavit, in points
const SIG_BELOW_LINE = 0.18;    // part of the signature's height that crosses below the line
const READY_GAP_PT = 18;        // space between the deponent's Date line and the "ready" message
const READY_ON_PAGE_MIN_PX = 560; // narrower pages (phones): the message goes under the document
// Filled-in blanks: the ownership %s and the financial year-end (backend FIELD_FONT_SIZE /
// FIELD_BASELINE_UP) and the level tick (backend TICK / TICK_PEN / tick_box)
const FIELD_FONT_PT = 12;
const FIELD_BASELINE_UP = 1.5;
const TICK = [[0.02, 0.55], [0.12, 0.62], [0.24, 0.80], [0.34, 0.98], [0.48, 0.62], [0.72, 0.25], [1.0, -0.06]];
const TICK_PEN = 0.1;

const tickPath = (cell) => {
  const h = cell.h * 0.92;
  const w = Math.min(cell.w * 0.8, h * 2.0);
  const x = cell.x + (cell.w - w) / 2;
  const y = cell.y + (cell.h - h) / 2;
  const p = TICK.map(([px, py]) => `${x + px * w} ${y + py * h}`);
  return { d: `M ${p[0]} C ${p[1]} ${p[2]} ${p[3]} C ${p[4]} ${p[5]} ${p[6]}`, pen: h * TICK_PEN };
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const formatDate = (d) => format(d, "dd/MM/yyyy");

// Backend COMMISSIONERS (server.py)
const COMMISSIONERS = [
  { key: "schalk", name: "Schalk Coetzee", title: "CA(SA), RA", email: "schalk@tcauditors.co.za" },
  { key: "ruan", name: "Ruan England", title: "AGA(SA)", email: "ruan@tcauditors.co.za" },
];
const COMMISSIONER_BUTTON_H = 24; // points: the button above the Commissioner of Oaths line

// Position an overlay over the page, in % of the page (PDF points, top-left origin)
const boxStyle = (b, page) => ({
  left: `${(b.x / page.w) * 100}%`,
  top: `${(b.y / page.h) * 100}%`,
  width: `${(b.w / page.w) * 100}%`,
  height: `${(b.h / page.h) * 100}%`,
});

// The strokes drawn on the big pad -> a transparent PNG with the signature box's proportions: the
// signature cropped to its strokes, as large as fits (most of it above the line, its lowest strokes
// just crossing it, as a pen signature does) and redrawn with an even pen line.
const fitSignature = (strokes, box) => {
  const points = strokes.flat();
  if (!points.length) return null;
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const x0 = Math.min(...xs);
  const y0 = Math.min(...ys);
  const tw = Math.max(Math.max(...xs) - x0, 1);
  const th = Math.max(Math.max(...ys) - y0, 1);

  const out = document.createElement("canvas");
  out.width = EXPORT_W;
  out.height = Math.round((EXPORT_W * box.h) / box.w);
  const pxPerPt = EXPORT_W / box.w;
  const pen = PEN_PT * pxPerPt;
  const lineY = (box.line_y - box.y) * pxPerPt;
  const maxW = out.width * 0.94 - pen;
  const maxH = Math.min(
    (lineY - pen) / (1 - SIG_BELOW_LINE),           // room above the line
    (out.height - pen - lineY) / SIG_BELOW_LINE,    // room below it, inside the box
  );
  const k = Math.min(maxW / tw, maxH / th);
  const w = tw * k;
  const h = th * k;
  const left = (out.width - w) / 2;
  const top = lineY + SIG_BELOW_LINE * h - h;
  const at = (p) => [left + (p.x - x0) * k, top + (p.y - y0) * k];

  const ctx = out.getContext("2d");
  ctx.strokeStyle = INK;
  ctx.fillStyle = INK;
  ctx.lineWidth = pen;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  strokes.forEach((stroke) => {
    if (stroke.length === 1) {
      const [x, y] = at(stroke[0]);
      ctx.beginPath();
      ctx.arc(x, y, pen / 2, 0, Math.PI * 2);
      ctx.fill();
      return;
    }
    // Smooth curve through the midpoints between the recorded points
    ctx.beginPath();
    let [px, py] = at(stroke[0]);
    ctx.moveTo(px, py);
    for (let i = 1; i < stroke.length; i += 1) {
      const [x, y] = at(stroke[i]);
      ctx.quadraticCurveTo(px, py, (px + x) / 2, (py + y) / 2);
      [px, py] = [x, y];
    }
    ctx.lineTo(px, py);
    ctx.stroke();
  });
  return out.toDataURL("image/png");
};

const isTouch = () => window.matchMedia("(pointer: coarse)").matches;

// The pad keeps (and changes) its own stroke list: work with copies, so Cancel leaves the saved signature
const copyStrokes = (strokes) => strokes.map((stroke) => stroke.map((p) => ({ ...p })));

const useElementWidth = () => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    // Measured straight away and on window resize as well: ResizeObserver doesn't report while
    // the tab is in the background
    const measure = () => setWidth(Math.round(el.getBoundingClientRect().width));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);
  return [ref, width];
};

// The Deponent Signature block: click it to sign on the big pad (owner: the block itself is too
// small to sign in with a mouse). Signed: the signature shows on the line; click to sign again.
const SignatureBox = ({ box, page, signed, onOpenPad, onClear, highlight }) => (
  <div
    id="signature-box"
    className={`bee-sig-box${signed ? " is-signed" : ""}${highlight ? " is-missing" : ""}`}
    style={boxStyle(box, page)}
    data-testid="signature-box"
  >
    <button
      type="button"
      className="bee-sig-tap"
      onClick={onOpenPad}
      aria-label={signed ? "Sign again as deponent" : "Sign as deponent"}
      title={signed ? "Click to sign again" : undefined}
    />
    {!signed && <span className="bee-sig-hint">{isTouch() ? "Tap here to sign" : "Click here to sign"}</span>}
    {signed && (
      <button type="button" className="bee-sig-clear" onClick={onClear} data-testid="button-clear-signature">
        <Eraser className="w-3.5 h-3.5" /> <span>Clear</span>
      </button>
    )}
  </div>
);

// The big signing pad (pop-up): sign comfortably, then Save puts the signature on the line
const SignaturePadModal = ({ strokes, onSave, onCancel }) => {
  const padRef = useRef(null);
  const [empty, setEmpty] = useState(!strokes);

  useEffect(() => {
    // Signing again: the signature so far, to add to or clear
    if (strokes && padRef.current) padRef.current.fromData(copyStrokes(strokes));
  }, [strokes]);

  return (
    <div className="modal-overlay" data-testid="signature-pad-dialog">
      <div className="modal-content bee-pad-modal">
        <h2>Deponent Signature</h2>
        <p className="modal-description">
          Sign in the box below {isTouch() ? "with your finger or stylus" : "with your mouse, touchpad or stylus"}, then
          click <strong>Save</strong>: your signature is placed on the Deponent Signature line.
        </p>
        <div className="bee-pad">
          <SignatureCanvas
            ref={padRef}
            penColor={INK}
            minWidth={1.5}
            maxWidth={3.5}
            canvasProps={{ className: "bee-sig-canvas", "aria-label": "Deponent signature pad" }}
            onEnd={() => setEmpty(false)}
          />
          <span className="bee-pad-line" />
        </div>
        <div className="modal-actions bee-pad-actions">
          <button type="button" className="bee-btn-plain" onClick={() => { padRef.current.clear(); setEmpty(true); }} data-testid="button-pad-clear">Clear</button>
          <button type="button" className="bee-btn-plain" onClick={onCancel}>Cancel</button>
          <button
            type="button"
            className="btn-shimmer-orange bee-btn-done"
            onClick={() => onSave(empty || padRef.current.isEmpty() ? null : copyStrokes(padRef.current.toData()))}
            data-testid="button-signature-save"
          >
            <span>Save</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Everything signed (deponent signature + commissioner): a gently "breathing" green note
const ReadyMessage = ({ style, className = "" }) => (
  <div className={`bee-ready ${className}`} style={style} role="status" data-testid="ready-message">
    <Check className="bee-ready-icon" />
    <span>
      <strong>Excellent, all signed and ready.</strong>
      <br />
      Click on the orange button below to Print to PDF and Send.
    </span>
  </div>
);

// The financial year-end: latest February by default (owner), shown handwritten (SVG below); this
// invisible button over its blank opens a month / year choice (businesses with another year-end)
const YearEndBox = ({ spot, page, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const choices = yearEndChoices();
  const years = [...new Set(choices.map((c) => c.slice(0, 4)))];
  const [year, month] = value.split("-");
  const pick = (y, m) => {
    const ym = `${y}-${m}`;
    // A month that hasn't ended yet in the chosen year: its latest month that has
    onChange(choices.includes(ym) ? ym : choices.find((c) => c.startsWith(`${y}-`)));
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="bee-date-box"
          style={boxStyle({ x: spot.x0, y: spot.baseline - 14, w: spot.x1 - spot.x0, h: 17 }, page)}
          aria-label={`Financial year-end ${formatYearEnd(value, spot.format)} - click to change`}
          title="Click to change the financial year-end"
          data-testid="year-end-box"
        />
      </PopoverTrigger>
      <PopoverContent className="w-auto bee-ye-pop" align="start">
        <p className="bee-ye-title">Financial year-end</p>
        <div className="bee-ye-selects">
          <select className="bee-cal-select" value={month} onChange={(e) => pick(year, e.target.value)} aria-label="Month" data-testid="year-end-month">
            {MONTHS.map((m, i) => {
              const mm = String(i + 1).padStart(2, "0");
              return <option key={mm} value={mm} disabled={!choices.includes(`${year}-${mm}`)}>{m}</option>;
            })}
          </select>
          <select className="bee-cal-select" value={year} onChange={(e) => pick(e.target.value, month)} aria-label="Year" data-testid="year-end-year">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <p className="bee-ye-hint">The year ends on the last day of the month.</p>
        <button type="button" className="bee-btn-small" onClick={() => setOpen(false)}>Done</button>
      </PopoverContent>
    </Popover>
  );
};

// The date: shown handwritten on the page (SVG below); this invisible button opens the calendar
const DateBox = ({ box, page, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const today = new Date();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="bee-date-box"
          style={boxStyle(box, page)}
          aria-label={`Date ${formatDate(value)} - click to change`}
          title="Click to change the date"
          data-testid="date-box"
        >
          <CalendarDays className="bee-date-icon" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <Calendar
          mode="single"
          selected={value}
          defaultMonth={value}
          onSelect={(d) => { if (d) { onChange(d); setOpen(false); } }}
          captionLayout="dropdown-buttons"
          fromDate={addDays(today, -365)}
          toDate={today}
          classNames={{
            caption_label: "hidden",
            caption_dropdowns: "bee-cal-dropdowns",
            dropdown: "bee-cal-select",
            vhidden: "sr-only",
            day_selected: "bee-cal-selected",
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
};

// Above the Commissioner of Oaths line: the commissioner who signs (only the PDF gets the signature
// and stamps). The commissioners themselves see their own name; others choose.
const CommissionerBox = ({ info, page, own, chosen, onChoose, highlight }) => {
  const c = info.commissioner;
  const style = boxStyle(
    { x: c.line_x0, y: c.line_y - COMMISSIONER_BUTTON_H - 3, w: c.line_x1 - c.line_x0, h: COMMISSIONER_BUTTON_H },
    page,
  );
  if (own) {
    // Schalk / Ruan: nothing to do here (owner's wording on the second line)
    return (
      <div className="bee-comm-box is-own" style={style} data-testid="commissioner-box">
        <span className="bee-comm-name">
          <PenLine className="bee-comm-icon" />
          <span className="bee-comm-lines">
            <span>Commissioner: <strong>{own.name}</strong></span>
            <em className="bee-comm-auto">Signing is automatic. No further action required here.</em>
          </span>
        </span>
      </div>
    );
  }
  return (
    <div className="bee-comm-box" style={style} data-testid="commissioner-box">
      <button
        id="commissioner-button"
        type="button"
        onClick={onChoose}
        className={`${chosen ? "bee-comm-chosen" : "btn-shimmer-orange bee-comm-btn"}${highlight ? " is-missing" : ""}`}
        title={chosen ? "Click to choose a different Commissioner" : undefined}
        data-testid="button-choose-commissioner"
      >
        <PenLine className="bee-comm-icon" />
        {chosen ? <span>Commissioner: <strong>{chosen.name}</strong></span> : <span>Choose your Commissioner</span>}
      </button>
    </div>
  );
};

const CommissionerModal = ({ chosen, onSelect, onCancel }) => (
  <div className="modal-overlay" data-testid="commissioner-dialog" onClick={onCancel}>
    <div className="modal-content bee-comm-modal" onClick={(e) => e.stopPropagation()}>
      <h2>Choose your Commissioner</h2>
      <p className="modal-description">Click on the Commissioner of Oaths who must sign and stamp this affidavit.</p>
      <div className="bee-comm-options">
        {COMMISSIONERS.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`bee-comm-option${chosen?.key === c.key ? " is-selected" : ""}`}
            onClick={() => onSelect(c)}
            data-testid={`commissioner-${c.key}`}
          >
            <span className="bee-comm-option-name">{c.name}</span>
            <span className="bee-comm-option-title">{c.title} &middot; Commissioner of Oaths</span>
            {chosen?.key === c.key && <Check className="bee-comm-option-check" />}
          </button>
        ))}
      </div>
      <div className="modal-actions">
        <button type="button" className="bee-btn-plain" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  </div>
);

const SignAffidavitPage = ({ slug, info }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, expired } = useAuth();
  // Black ownership (chosen on the front page) and page 1's details (Company Details page): this page
  // only opens with both (SignAffidavit below). Ownership can be changed here (Change); the details on
  // the Company Details page (Change -> back there, with what was typed).
  const [ownership, setOwnership] = useState(location.state.ownership);
  const [ownOpen, setOwnOpen] = useState(false);
  const { details, form, source, invoice } = location.state;
  // Sent with the affidavit: trying again after a failure doesn't create a second invoice (backend)
  const [invoiceRequestId] = useState(newRequestId);
  const invoicing = invoice.charge && !!invoice.contact_id;
  const [yearEnd, setYearEnd] = useState(latestFebruaryYearEnd);
  const ownCommissioner = COMMISSIONERS.find((c) => c.email === user.email.toLowerCase()) || null;
  const [chosenCommissioner, setChosenCommissioner] = useState(null);
  const [commOpen, setCommOpen] = useState(false);
  const [commMissing, setCommMissing] = useState(false);
  const [signature, setSignature] = useState(null);   // PNG (data URL) fitted to the signature box
  const [strokes, setStrokes] = useState(null);       // what was drawn on the big pad (to sign again)
  const [signedDate, setSignedDate] = useState(() => new Date());
  const [padOpen, setPadOpen] = useState(false);
  const [missing, setMissing] = useState(false);
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState(null);
  const [result, setResult] = useState(null);
  const [sigPageRef, sigPageWidth] = useElementWidth();

  const sig = info.signature;
  const dt = info.date;
  const comm = info.commissioner;
  const commissioner = ownCommissioner || chosenCommissioner;
  const ready = !!signature && !!commissioner;
  const readyOnPage = sigPageWidth >= READY_ON_PAGE_MIN_PX;

  // What is handwritten into the blanks (the backend prints the same: fill_ins in server.py)
  const fieldValue = { black: ownership ? pct(ownership.black) : "", woman: ownership ? pct(ownership.woman) : "", designated: "0", sub: "0" };
  const level = ownership ? levelFor(slug, ownership.black) : null;
  const tickCell = info.levels.find((l) => l.level === level);
  const ye = info.year_end;
  // Page 1's details, printed in bold in the table (the backend prints the same: fit_detail in server.py)
  const detailBase = detailBaseSize(info.details);
  const detailText = Object.entries(detailValues(details, slug))
    .filter(([key, text]) => info.details[key] && text)
    .map(([key, text]) => ({ key, page: info.details[key].page, ...fitDetail(text, info.details[key], detailBase) }));
  const changeDetails = () => navigate(`/affidavit/${slug}/details`, { state: { ownership, form } });

  const saveSignature = useCallback((drawn) => {
    setStrokes(drawn);
    setSignature(drawn ? fitSignature(drawn, sig) : null);
    if (drawn) setMissing(false);
    setPadOpen(false);
  }, [sig]);

  const clearSignature = () => {
    setStrokes(null);
    setSignature(null);
  };

  const handleSend = async () => {
    if (!ownership) {
      setOwnOpen(true);
      return;
    }
    if (!signature) {
      setMissing(true);
      setProblem({
        title: "Signature needed",
        text: "Please click the Deponent Signature block on page 2 and sign before clicking Print to PDF and Send.",
      });
      return;
    }
    if (!commissioner) {
      setCommMissing(true);
      setProblem({
        title: "Commissioner needed",
        text: "Please click Choose your Commissioner on page 2 (above the Commissioner of Oaths line) before clicking Print to PDF and Send.",
      });
      return;
    }
    setSending(true);
    try {
      const data = await api("/affidavit/send", {
        method: "POST",
        body: {
          slug,
          signed_date: formatDate(signedDate),
          signature_png: signature.split(",")[1],
          commissioner: commissioner.key,
          ownership: { black: ownership.black, woman: ownership.woman },
          year_end: yearEnd,
          details,
          source,
          invoice: invoice.charge
            ? { charge: true, action: invoice.action, delivery: invoice.delivery, contact_id: invoice.contact_id, request_id: invoiceRequestId }
            : { charge: false },
        },
      });
      // Owner: the signed PDF is never shown or printed on the website - only the email has it
      setResult({ recipients: data.recipients || [], invoice: data.invoice });
    } catch (err) {
      if (err.status === 401) {
        // Signed out meanwhile (8-hour session, or the administrator changed the account)
        expired();
        return;
      }
      setProblem({
        title: "Not sent",
        text: `${err.message} Please try again, or contact ${ADMIN_EMAIL}.`,
      });
    } finally {
      setSending(false);
    }
  };

  const closeProblem = () => {
    setProblem(null);
    if (missing) document.getElementById("signature-box")?.scrollIntoView({ block: "center" });
    else if (commMissing) document.getElementById("commissioner-button")?.scrollIntoView({ block: "center" });
  };

  // OK: back to the front page, to choose the next affidavit
  const handleDone = () => navigate("/");

  // At the top, and again at the bottom on the left above "Signed in as" (owner, 2026-10-11)
  const back = (place) => (
    <Link to="/" className="btn-shimmer-orange bee-back-btn" data-testid={place === "top" ? "link-back" : "link-back-bottom"}>
      <ArrowLeft className="w-5 h-5" /> <span>Choose A Different Affidavit</span>
    </Link>
  );

  return (
    <PageFrame title="B-BBEE Sworn Affidavit" subtitle={info.name} bottom={back("bottom")}>
      <div className="bee-sign-top">{back("top")}</div>

      <section className="bee-steps" aria-label="How to sign">
        <ol className="bee-numbered">
          <li>Click the <strong>Deponent Signature</strong> block on page 2 and sign on the large signing pad that opens.</li>
          <li>The date is today's date. Click on it to choose a different date.</li>
          <li>
            The company details (page 1), the black ownership, the financial year-end (the latest February year-end;
            click on it to change it) and the B-BBEE level tick are filled in for you.
          </li>
          {!ownCommissioner && (
            <li>Click <strong>Choose your Commissioner</strong> above the Commissioner of Oaths line on page 2 and choose who must sign and stamp the affidavit.</li>
          )}
          <li>Click <strong>Print to PDF and Send</strong>: the signed affidavit is prepared as a PDF and emailed.</li>
        </ol>
      </section>

      <div className="bee-own-bar" data-testid="company-bar">
        {/* One row each, labels bold and values plain (owner, 2026-10-11) */}
        <dl className="bee-company-rows" data-testid="company-rows">
          {[
            ["Company:", `${details.enterprise_name} (${details.registration_number})`],
            ["Deponent:", details.deponent_name],
            ...invoiceRows(invoice),
          ].map(([label, value]) => (
            <div key={label} className="bee-company-row">
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
        <button type="button" className="bee-btn-small" onClick={changeDetails} data-testid="button-change-details">
          <PenLine size={14} /> <span>Change</span>
        </button>
      </div>

      {ownership && (
        <div className="bee-own-bar" data-testid="ownership-bar">
          <span>
            <strong>{pct(ownership.black)}%</strong> black owned ({pct(ownership.woman)}% black woman owned),{" "}
            <strong>{pct(100 - ownership.black)}%</strong> white owned
            {level && <> &middot; <strong>{LEVEL_NAMES[level]}</strong></>}
          </span>
          <button type="button" className="bee-btn-small" onClick={() => setOwnOpen(true)} data-testid="button-change-ownership">
            <PenLine size={14} /> <span>Change</span>
          </button>
        </div>
      )}

      <div className="bee-doc" data-testid="affidavit-document">
        {info.pages.map((page, i) => (
          <div key={page.image} ref={i === sig.page ? sigPageRef : undefined} className="bee-page" style={{ aspectRatio: `${page.w} / ${page.h}` }} data-testid={`page-${i + 1}`}>
            <img src={page.image} alt={`${info.name} affidavit, page ${i + 1} of ${info.pages.length}`} draggable={false} />
            {(i === sig.page || i === dt.page || ownership || detailText.some((d) => d.page === i)) && (
              <svg className="bee-page-ink" viewBox={`0 0 ${page.w} ${page.h}`} aria-hidden="true">
                {/* Page 1's details: Carlito Bold, black (owner: readable - not handwritten) */}
                {detailText.filter((d) => d.page === i).map((d) => d.lines.map((l) => (
                  <text key={`${d.key}-${l.y}`} x={l.x} y={l.y} className="bee-typed" fontSize={d.size} fill={DATE_INK} data-testid={`detail-${d.key}`}>
                    {l.text}
                  </text>
                )))}
                {/* Ownership %s, financial year-end and the level tick, handwritten in bold black */}
                {ownership && info.fields.filter((f) => f.page === i).map((f) => (
                  <text key={`${f.kind}-${f.baseline}`} x={(f.x0 + f.x1) / 2} y={f.baseline - FIELD_BASELINE_UP} textAnchor="middle" className="bee-hand" fontSize={FIELD_FONT_PT} fill={DATE_INK} stroke={DATE_INK} strokeWidth={FIELD_FONT_PT * HAND_BOLD} strokeLinejoin="round" data-testid={`field-${f.kind}`}>
                    {fieldValue[f.kind]}
                  </text>
                ))}
                {ownership && ye.page === i && (
                  <text x={(ye.x0 + ye.x1) / 2} y={ye.baseline - FIELD_BASELINE_UP} textAnchor="middle" className="bee-hand" fontSize={FIELD_FONT_PT} fill={DATE_INK} stroke={DATE_INK} strokeWidth={FIELD_FONT_PT * HAND_BOLD} strokeLinejoin="round" data-testid="year-end-text">
                    {formatYearEnd(yearEnd, ye.format)}
                  </text>
                )}
                {ownership && tickCell && tickCell.page === i && (
                  <path d={tickPath(tickCell).d} fill="none" stroke={DATE_INK} strokeWidth={tickPath(tickCell).pen} strokeLinecap="round" strokeLinejoin="round" data-testid={`tick-level-${level}`} />
                )}
                {i === sig.page && signature && (
                  <image href={signature} x={sig.x} y={sig.y} width={sig.w} height={sig.h} preserveAspectRatio="xMidYMid meet" className="bee-sig-image" />
                )}
                {i === dt.page && (
                  <text x={dt.x + DATE_X_PAD} y={dt.line_y - DATE_BASELINE_UP} className="bee-hand" fontSize={DATE_FONT_PT} fill={DATE_INK} stroke={DATE_INK} strokeWidth={DATE_FONT_PT * HAND_BOLD} strokeLinejoin="round" data-testid="date-text">
                    {formatDate(signedDate)}
                  </text>
                )}
              </svg>
            )}
            {i === sig.page && (
              <SignatureBox
                box={sig}
                page={page}
                signed={!!signature}
                onOpenPad={() => setPadOpen(true)}
                onClear={clearSignature}
                highlight={missing}
              />
            )}
            {i === dt.page && <DateBox box={dt} page={page} value={signedDate} onChange={setSignedDate} />}
            {ownership && i === ye.page && <YearEndBox spot={ye} page={page} value={yearEnd} onChange={setYearEnd} />}
            {/* Below the deponent's date, with some space (owner) */}
            {i === dt.page && ready && readyOnPage && (
              <ReadyMessage
                style={{
                  left: `${((dt.x - 15) / page.w) * 100}%`,
                  top: `${((dt.line_y + READY_GAP_PT) / page.h) * 100}%`,
                  width: `${((dt.w + 15) / page.w) * 100}%`,
                }}
                className="is-on-page"
              />
            )}
            {i === comm.page && (
              <CommissionerBox
                info={info}
                page={page}
                own={ownCommissioner}
                chosen={chosenCommissioner}
                onChoose={() => setCommOpen(true)}
                highlight={commMissing}
              />
            )}
          </div>
        ))}
      </div>

      {ready && !readyOnPage && <ReadyMessage className="is-below-doc" />}

      <div className="bee-actions">
        <button type="button" onClick={handleSend} disabled={sending} className="btn-shimmer-orange bee-send-btn" data-testid="button-send-print">
          <Send className="w-5 h-5" />
          <span>{sending ? "Sending..." : "Print to PDF and Send"}</span>
          <Printer className="w-5 h-5" />
        </button>
      </div>

      {padOpen && (
        <SignaturePadModal
          strokes={strokes}
          onCancel={() => setPadOpen(false)}
          onSave={saveSignature}
        />
      )}

      {ownOpen && (
        <OwnershipModal
          slug={slug}
          name={info.name}
          onCancel={() => setOwnOpen(false)}
          onDone={(o) => { setOwnership(o); setOwnOpen(false); }}
        />
      )}

      {commOpen && (
        <CommissionerModal
          chosen={chosenCommissioner}
          onCancel={() => setCommOpen(false)}
          onSelect={(c) => { setChosenCommissioner(c); setCommMissing(false); setCommOpen(false); }}
        />
      )}

      {sending && (
        <div className="bee-sending-overlay" data-testid="sending-overlay">
          <div className="spinner" />
          <p className="bee-sending-title">Preparing and sending your signed affidavit...</p>
          <p className="bee-sending-text">
            {invoicing
              ? "Please wait while the signed PDF is prepared and emailed, and the invoice is created in Xero."
              : "Please wait while the signed PDF is prepared and emailed."}
          </p>
        </div>
      )}

      {problem && (
        <div className="modal-overlay" data-testid="problem-dialog" onClick={closeProblem}>
          <div className="modal-content bee-problem-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{problem.title}</h2>
            <p className="modal-description">{problem.text}</p>
            <div className="modal-actions">
              <button type="button" className="send-btn" onClick={closeProblem} data-testid="button-problem-ok">OK</button>
            </div>
          </div>
        </div>
      )}

      {result && (
        <div className="modal-overlay" data-testid="success-dialog">
          <div className="modal-content success-modal">
            <h2>Affidavit Signed and Sent</h2>
            <p className="modal-description">Thank you. Your signed B-BBEE affidavit was sent successfully.</p>
            <div className="email-recipients-confirmation" data-testid="email-recipients-confirmation">
              <p className="email-recipients-heading">The signed affidavit was emailed to:</p>
              {result.recipients.map((r) => (
                <div key={r.email} className="email-recipient-item">
                  <Check className="email-recipient-check" />
                  <span>{r.label}: <strong>{r.email}</strong></span>
                </div>
              ))}
            </div>
            {/* Owner: what happened to the invoice in Xero - also when it failed */}
            {result.invoice?.requested && (
              <div
                className={`bee-invoice-result ${result.invoice.ok ? "is-ok" : result.invoice.number || invoice.contact_id ? "is-failed" : "is-manual"}`}
                role={result.invoice.ok ? "status" : "alert"}
                data-testid="invoice-result"
              >
                <p className="bee-invoice-result-head">
                  {result.invoice.ok ? <Check className="email-recipient-check" /> : <AlertTriangle size={18} />}
                  <strong>
                    {result.invoice.ok ? "Invoice created in Xero"
                      : result.invoice.number ? "Invoice not sent"
                        : invoice.contact_id ? "Invoice creation failed" : "No invoice created"}
                  </strong>
                </p>
                <p>{result.invoice.message}</p>
              </div>
            )}
            <button type="button" onClick={handleDone} className="bee-ok-btn" data-testid="button-ok">
              OK
            </button>
          </div>
        </div>
      )}
    </PageFrame>
  );
};

const SignAffidavit = () => {
  const { slug } = useParams();
  const location = useLocation();
  const info = LAYOUT[slug];
  if (!info) return <Navigate to="/" replace />;
  // Opened directly (a link, or without the earlier steps): the Company Details page first, which also
  // asks for the black ownership when it is missing
  const { ownership, details, invoice } = location.state || {};
  const ownershipOk = ownership && !(isQse(slug) && ownership.black < MIN_QSE_BLACK);
  if (!ownershipOk || !details || !invoice) {
    return <Navigate to={`/affidavit/${slug}/details`} replace state={{ ownership: ownershipOk ? ownership : null, form: location.state?.form }} />;
  }
  // key: a fresh page (no leftover signature) for each affidavit
  return <SignAffidavitPage key={slug} slug={slug} info={info} />;
};

export default SignAffidavit;
